import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { RENTAL_RESULTS, RENTAL_RESULT_LABEL, type RentalAnswers } from '../../lib/rental-inspections';
import type { RentalChecklistResponse, RentalInspectionType, RentalItemResult } from '../../types/rental-inspection';

/**
 * One rental asset's inspection checklist, answered Pass / Minor / Fail / N/A.
 *
 * The items are the server's (api#861): the base list for this inspection
 * type, plus the add-ons for the asset's category family when the tenant has
 * set one. Which list is shown is said in words above it, so a base-only list
 * is not mistaken for a complete one for, say, a tank.
 *
 * Controlled: the parent holds the answers (several assets per screen on
 * Delivery and Return) and learns the checklist through `onChecklist`, which
 * it needs to build the POST body and to know when every item is answered.
 */
export default function RentalAssetChecklist({
  assetId,
  inspectionType,
  answers,
  onChange,
  onChecklist,
  compact = false,
}: {
  assetId: string;
  inspectionType: RentalInspectionType;
  answers: RentalAnswers;
  onChange: (next: RentalAnswers) => void;
  onChecklist: (cl: RentalChecklistResponse) => void;
  /** Field screens: tighter spacing. */
  compact?: boolean;
}) {
  const query = useQuery<RentalChecklistResponse>({
    queryKey: ['rentals', 'inspection-checklist', assetId, inspectionType],
    queryFn: () =>
      api.rentals.inspections.checklist(assetId, inspectionType).then((r) => {
        const d = r.data?.data;
        if (!d) throw new Error('The server answered with no checklist.');
        return d;
      }),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const cl = query.data;
  // `onChecklist` must be stable (useCallback) in the parent, or this re-runs
  // every render; parents here pass a setter-backed callback.
  useEffect(() => {
    if (cl) onChecklist(cl);
  }, [cl, onChecklist]);

  const failure = queryLoadFailure(query);
  if (failure) return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this asset's inspection checklist" />;
  if (!cl) return <div className="flex justify-center py-3"><Loader2 className="size-4 animate-spin text-fieldiq-muted" /></div>;

  const set = (key: string, patch: Partial<{ result: RentalItemResult | null; notes: string }>) =>
    onChange({ ...answers, [key]: { result: answers[key]?.result ?? null, notes: answers[key]?.notes ?? '', ...patch } });

  const sections: { title: string; items: RentalChecklistResponse['items'] }[] = [
    { title: 'Every asset', items: cl.items.filter((i) => i.section === 'base') },
    ...(cl.family ? [{ title: cl.family_label ?? 'Family', items: cl.items.filter((i) => i.section === 'family') }] : []),
  ];

  return (
    <div data-testid={`rental-checklist-${assetId}`} className="space-y-3">
      <p className="text-xs text-fieldiq-muted">
        {cl.family
          ? `${cl.family_label} checklist (category: ${cl.category ?? '—'})`
          : `Base checklist only — ${cl.category ? `the category "${cl.category}" has no checklist family set` : 'this asset has no category'}.`}
      </p>
      {sections.map((sec) => (
        <fieldset key={sec.title} className="space-y-2">
          <legend className="text-[0.68rem] font-black uppercase tracking-[0.06em] text-fieldiq-muted">{sec.title}</legend>
          {sec.items.map((item) => {
            const a = answers[item.key];
            return (
              <div key={item.key} className={`rounded-lg border border-fieldiq-border bg-fieldiq-dark ${compact ? 'p-2' : 'p-3'}`}>
                <p className="text-sm text-fieldiq-text">{item.label}</p>
                <div role="radiogroup" aria-label={item.label} className="mt-2 grid grid-cols-4 gap-1.5">
                  {RENTAL_RESULTS.map((r) => {
                    const on = a?.result === r;
                    const fill = r === 'pass' ? 'bg-fi-signal-green' : r === 'minor' ? 'bg-fi-signal-amber' : r === 'fail' ? 'bg-fi-signal-red' : 'bg-fi-signal-slate';
                    return (
                      <button
                        key={r}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => set(item.key, { result: r })}
                        className={`min-h-[40px] rounded-lg border text-xs font-semibold ${on ? `border-black/20 ${fill} text-fi-signal-ink` : 'border-fieldiq-border text-fieldiq-text'}`}
                      >
                        {RENTAL_RESULT_LABEL[r]}
                      </button>
                    );
                  })}
                </div>
                {(a?.result === 'minor' || a?.result === 'fail') && (
                  <input
                    aria-label={`Notes on ${item.label}`}
                    value={a.notes}
                    onChange={(e) => set(item.key, { notes: e.target.value })}
                    placeholder="What is wrong (optional)"
                    className="mt-2 w-full rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
                  />
                )}
              </div>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
